/**
 * Edit Task Dialog
 *
 * Modal form for editing an existing agent task's title, description,
 * priority, and tags.
 *
 * @module features/tasks/components/EditTaskDialog
 */

import React from 'react';
import { Dialog, DialogTrigger, DialogContent, DialogFooter, Button, TextField, TextArea, Select, useToast } from '@chatsdet/ui';
import { PencilSimple } from '@phosphor-icons/react';
import { useUpdateTask } from '../hooks/useTasks';
import type { AgentTaskDetail } from '../types';

const PRIORITY_OPTIONS = [
  { id: '0', label: 'Normal' },
  { id: '1', label: 'High' },
  { id: '2', label: 'Critical' },
];

interface EditTaskDialogProps {
  task: AgentTaskDetail;
}

export function EditTaskDialog({ task }: EditTaskDialogProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [title, setTitle] = React.useState(task.title);
  const [description, setDescription] = React.useState(task.description);
  const [priority, setPriority] = React.useState(String(task.priority));
  const [tags, setTags] = React.useState(task.tags.join(', '));
  const [assignedTo, setAssignedTo] = React.useState(task.assignedTo ?? '');

  const updateTask = useUpdateTask();
  const { toast } = useToast();

  // Re-sync form when task data changes or dialog opens
  React.useEffect(() => {
    if (isOpen) {
      setTitle(task.title);
      setDescription(task.description);
      setPriority(String(task.priority));
      setTags(task.tags.join(', '));
      setAssignedTo(task.assignedTo ?? '');
    }
  }, [isOpen, task.title, task.description, task.priority, task.tags, task.assignedTo]);

  const handleSubmit = () => {
    if (!title.trim() || !description.trim()) return;

    const parsedTags = tags
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);

    updateTask.mutate(
      {
        id: task.id,
        title: title.trim(),
        description: description.trim(),
        priority: parseInt(priority, 10),
        tags: parsedTags,
        assignedTo: assignedTo.trim() || null,
      },
      {
        onSuccess: () => {
          toast.success('Task updated');
          setIsOpen(false);
        },
        onError: () => {
          toast.error('Failed to update task');
        },
      }
    );
  };

  const canSubmit = !!title.trim() && !!description.trim() && !updateTask.isPending;

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen} size="lg">
      <Button variant="ghost" aria-label="Edit task" className="shrink-0">
        <PencilSimple size={18} />
      </Button>
      <Dialog title="Edit Task" description="Update the task details.">
        <DialogContent>
          <TextField
            label="Title"
            value={title}
            onChange={setTitle}
            placeholder="Brief description of the task"
          />
          <TextArea
            label="Description"
            value={description}
            onChange={setDescription}
            placeholder="What needs to be done and where in the codebase"
            className="min-h-[100px]"
          />
          <Select
            label="Priority"
            items={PRIORITY_OPTIONS}
            selectedKey={priority}
            onSelectionChange={(key) => setPriority(key as string)}
          />
          <TextField
            label="Assigned To"
            value={assignedTo}
            onChange={setAssignedTo}
            placeholder="Email address (leave empty to unassign)"
          />
          <TextField
            label="Tags"
            value={tags}
            onChange={setTags}
            placeholder="Comma-separated tags (e.g. cleanup, feature-flags)"
          />
        </DialogContent>
        <DialogFooter>
          <Button variant="outline" onPress={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onPress={handleSubmit}
            isDisabled={!canSubmit}
          >
            {updateTask.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </Dialog>
    </DialogTrigger>
  );
}
